import React, {FunctionComponent, ReactNode, useContext, useRef} from 'react';
import classNames from 'classnames';
import {Icon} from '../Icon';
import AccordionContext from './AccordionContext';
import {IconPlaceholder} from "../Icon/IconPlaceholder";
import {useAnalyticsTrackers} from "../../utils/useAnalyticsTrackers";

type AccordionItemProps = {
    children?: ReactNode,
    uuid: string,
    buttonClassName?: string,
    className?: string,
    accentColor?: string,
    primaryText?: string,
    rightAlignedContent?: ReactNode,
    HeaderComponent?: FunctionComponent<{ expanded: boolean, toggle: () => void }>,
    accordionHeight?: string,
    expandable?: boolean,
    isSingleAccordion?: boolean,
    sendUuidToAssetTitling?: Function,
    expandedAccordionItems?: Array<any>,
    isMainExpanded?: boolean
};

export default function AccordionItem({
                                          children,
                                          uuid,
                                          buttonClassName,
                                          HeaderComponent,
                                          className,
                                          accentColor,
                                          rightAlignedContent,
                                          primaryText,
                                          accordionHeight,
                                          expandable = true,
                                          isSingleAccordion = true,
                                          sendUuidToAssetTitling,
                                          expandedAccordionItems,
                                          isMainExpanded
                                      }: AccordionItemProps) {
    const {expandedItems, toggleItem} = useContext(AccordionContext);
    const openTimeRef = useRef<number | null>(null);
    const {page} = useAnalyticsTrackers();
    let count = 0;
    const toggle = () => {
        if (expandable) {
            toggleItem(uuid);
        }
        if (!isExpanded) {
            openTimeRef.current = window.performance.now();
        } else if (openTimeRef.current && primaryText && count === 0) {
            count++
            const duration = Math.round((window.performance.now() - openTimeRef.current) / 1000);
            page(`Expand_${primaryText}_Accordion`, {cd4_duration: duration});
        }
        if (isExpanded && sendUuidToAssetTitling && uuid.startsWith("asset-titling")) {
            sendUuidToAssetTitling(uuid)
        }
    }

    // @ts-ignore
    let isExpanded: boolean;
    if (isMainExpanded) { //check if parent is expanded and then overwrite the condition to only check for expandedAccordionItems
        isExpanded = expandedAccordionItems ? expandedAccordionItems.includes(uuid) : false;
    } else {
        isExpanded = expandedItems.includes(uuid) || (expandedAccordionItems ? expandedAccordionItems.includes(uuid) : false);
    }
    const iconName = isExpanded ? 'chevron_down' : 'chevron_right';
    const icon = expandable ? <Icon name={iconName} size="large"/> : <IconPlaceholder/>;
    const Header = (HeaderComponent)
        ? <HeaderComponent expanded={isExpanded} toggle={toggle}/>
        : (
            <div className="accented-accordion-item__header">
                <span className="accented-accordion-item__left-content">
                    {icon}
                    <span className="paddingleft-md">
                        <div className="h4 fontweight-normal margin-xs">{primaryText}</div>
                    </span>
                </span>
                {rightAlignedContent && (
                    <span className="accented-accordion-item__right-content">{rightAlignedContent}</span>
                )}
            </div>
        );

    const AccordionItemWithoutAccent = (
        <li
            aria-label={`${primaryText} Accordion Item`}
            className={classNames(
                'accordion__item',
                {'accordion__item--expanded': isExpanded, 'accordion__item--collapsed': !isExpanded},
                accentColor ? className : null,
            )}
        >
            <div
                className={classNames('accordion__button', buttonClassName, expandable && 'accordion__button--expandable')}
                aria-pressed={isExpanded ? 'true' : 'false'}
                onClick={toggle}
                role="button"
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        toggle();
                    }
                }}
                tabIndex={0}
            >
                {Header}
            </div>
            <div
                className={`${isSingleAccordion ? 'accordion__content' : 'accordion__content-right-moved'}`}>{children}</div>
        </li>
    );

    if (accentColor) {
        return (
            <div style={{minHeight: accordionHeight}}
                 role="accordionitem"
                 className={classNames('accented-accordion-item', className)}>
                <div aria-label="accordion accent color"
                     className="accented-accordion-item__accent"
                     style={{backgroundColor: accentColor, minHeight: accordionHeight}}/>
                {AccordionItemWithoutAccent}
            </div>
        );
    } else {
        return AccordionItemWithoutAccent;
    }
}
